import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { partnerApi } from "../../../../../../../services/Api/Partner/partnerApi";
import { UserPermissions } from "../../../../../../../services/Api/Partner/partnerTypes";

interface ChangePermissionsModalProps {
	partnerId: number;
	userEmail: string;
	currentPermissions: UserPermissions[];
	onClose: () => void;
	refetchData: () => Promise<void>;
}

interface FormProps {
	permissions: UserPermissions[];
}

export const ChangePermissionsModal = ({
	partnerId,
	userEmail,
	currentPermissions,
	onClose,
	refetchData,
}: ChangePermissionsModalProps) => {
	const { t } = useTranslation("admin");
	const form = useForm<FormProps>({ defaultValues: { permissions: currentPermissions } });
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		formState: { errors },
		control,
		handleSubmit,
	} = form;
	const [loading, setLoading] = useState(false);
	const permissionOptions = [
		{
			label: t("enums.user.permission.BASIC"),
			value: UserPermissions.BASIC,
		},
		{
			label: t("enums.user.permission.USERS"),
			value: UserPermissions.USERS,
		},
		{
			label: t("enums.user.permission.MONEY_FLOW"),
			value: UserPermissions.MONEY_FLOW,
		},
	];

	const onSubmit = handleSubmit(async (data: FormProps) => {
		setLoading(true);
		try {
			await partnerApi.updateUserPermissions(partnerId, userEmail, data.permissions);
			await refetchData();
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			onClose();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal show onHide={onClose}>
			<FormDirty>
				<Modal.Header>
					<h5>{t("modules.partner.field.change_permissions.title")}</h5>
				</Modal.Header>
				<Modal.Body>
					<FormSelectGroup
						isMulti
						label={t("modules.partner.field.permissions.title")}
						name="permissions"
						errors={errors}
						options={permissionOptions}
						control={control}
						data-testid="permissions"
					/>
				</Modal.Body>
			</FormDirty>
			<Modal.Footer>
				<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
					{t("common.action.save", { ns: "lib" })}
				</ButtonLoading>
				<Button variant="light" onClick={onClose}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
